import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, PackageOpen, Download, CheckCircle2 } from "lucide-react";

const HC_TRIMS = ["5.5x8.5", "6x9"];
const TRIM_LABELS = { "5x8": "5 × 8 in", "5.25x8": "5.25 × 8 in", "5.5x8.5": "5.5 × 8.5 in", "6x9": "6 × 9 in" };

const INCLUDES = [
  "Kindle eBook (.epub)",
  "Paperback interior PDF",
  "Front cover + wraparound cover PDF",
  "Listing metadata (blurb, keywords, categories)",
  "Ownership certificate",
  "README + full KDP publishing guide",
];

async function errDetail(e, fallback) {
  const d = e?.response?.data;
  try { if (d instanceof Blob) { const j = JSON.parse(await d.text()); return j.detail || fallback; } } catch { /* ignore */ }
  return (typeof d?.detail === "string" ? d.detail : fallback);
}

export default function KdpKitDialog({ open, onOpenChange, project, defaultLegalName = "" }) {
  const [binding, setBinding] = useState("paperback");
  const [trims, setTrims] = useState(["6x9"]);
  const [paper, setPaper] = useState("cream");
  const [legalName, setLegalName] = useState("");
  const [busy, setBusy] = useState(false);
  const [includeActs, setIncludeActs] = useState(false);
  const hasActs = (project?.acts || []).length > 0;
  const [freshMeta, setFreshMeta] = useState(null);
  const hasMeta = !!((freshMeta || project?.kdp_metadata)?.blurb);

  const wantHc = binding === "hardcover" || binding === "both";
  const trimOptions = binding === "hardcover" ? HC_TRIMS : Object.keys(TRIM_LABELS);
  // In "both", warn if a chosen trim can't produce a hardcover.
  const hcSkippedTrims = binding === "both" ? trims.filter((t) => !HC_TRIMS.includes(t)) : [];

  useEffect(() => { if (open) setLegalName(defaultLegalName || ""); }, [open, defaultLegalName]);
  const [blurb, setBlurb] = useState("");
  useEffect(() => {
    if (!open || !project?.id) return;
    const pick = (p) => p?.back_cover_blurb || p?.kdp_metadata?.blurb || p?.description || "";
    setBlurb(pick(project));
    api.get(`/projects/${project.id}`).then(({ data }) => { setBlurb(pick(data)); setFreshMeta(data?.kdp_metadata || {}); }).catch(() => {});
  }, [open, project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onBinding = (b) => {
    setBinding(b);
    if (b === "hardcover") {
      setTrims((prev) => {
        const kept = prev.filter((t) => HC_TRIMS.includes(t));
        return kept.length ? kept : ["6x9"];
      });
      if (paper === "groundwood") setPaper("cream");
    }
  };

  const toggleTrim = (t) => {
    setTrims((prev) => {
      if (prev.includes(t)) {
        const next = prev.filter((x) => x !== t);
        return next.length ? next : prev; // always keep at least one
      }
      return [...prev, t];
    });
  };

  const build = async () => {
    setBusy(true);
    try {
      const base = `/projects/${project.id}/kdp-kit`;
      const { data: start } = await api.post(`${base}/start`, { trims, paper, legal_name: legalName.trim(), binding, blurb: blurb.trim(), include_acts: hasActs && includeActs });
      const t0 = Date.now();
      for (;;) {
        await new Promise((r) => setTimeout(r, 4000));
        let st;
        try { ({ data: st } = await api.get(`${base}/jobs/${start.job_id}`)); } catch { if (Date.now() - t0 > 900000) throw new Error("The kit is taking too long — please try again."); continue; }
        if (st.status === "done") break;
        if (st.status === "error") throw new Error(st.error || "Couldn't build the KDP kit — please try again.");
        if (Date.now() - t0 > 900000) throw new Error("The kit is taking too long — please try again.");
      }
      const res = await api.get(`${base}/jobs/${start.job_id}/download`, { responseType: "blob", timeout: 180000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (project.title || "book").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "") || "book";
      a.download = `${safe}_KDP_Kit.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("KDP kit downloaded");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response ? await errDetail(e, "Couldn't build the KDP kit — please try again.") : (e?.message || "Couldn't build the KDP kit — please try again."));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="bg-popover max-w-lg" data-testid="kdp-kit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <PackageOpen className="w-5 h-5 text-primary" /> Download KDP kit
          </DialogTitle>
          <DialogDescription>
            One ZIP with everything you need to publish this book on Amazon KDP, named and organised, with a step-by-step guide.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="rounded-lg border border-border bg-secondary/30 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-primary mb-1.5">What's inside</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              {INCLUDES.map((t) => (
                <li key={t} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-500" /> {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-1.5">
            <Label>Binding</Label>
            <div className="grid grid-cols-3 gap-2" data-testid="kit-binding-toggle">
              {[["paperback", "Paperback"], ["hardcover", "Hardback"], ["both", "Both"]].map(([v, lbl]) => (
                <button key={v} type="button" onClick={() => onBinding(v)} data-testid={`kit-binding-${v}`}
                  className={`h-9 rounded-md border text-sm font-medium transition-colors ${binding === v ? "border-primary bg-primary/10 text-primary" : "border-input bg-background text-muted-foreground hover:text-foreground"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            {wantHc && (
              <p className="text-[11px] text-muted-foreground">
                Hardback (case laminate) needs 75+ pages and a KDP hardcover trim (5.5 × 8.5 or 6 × 9 in). Short or unsupported books ship paperback-only with a note.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <Label>Trim size(s) <span className="text-muted-foreground font-normal">— pick one or more to bundle</span></Label>
              <div className="grid grid-cols-2 gap-2" data-testid="kit-trim-multi">
                {trimOptions.map((t) => {
                  const on = trims.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleTrim(t)} data-testid={`kit-trim-${t}`}
                      className={`flex items-center justify-between h-9 px-3 rounded-md border text-sm font-medium transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-input bg-background text-muted-foreground hover:text-foreground"}`}>
                      <span>{TRIM_LABELS[t]}</span>
                      {on && <CheckCircle2 className="w-3.5 h-3.5 text-primary" />}
                    </button>
                  );
                })}
              </div>
              {trims.length > 1 && (
                <p className="text-[11px] text-muted-foreground" data-testid="kit-multi-note">
                  Each trim gets its own interior PDF + cover(s) in a <span className="font-medium">Trim_…</span> folder inside the kit.
                </p>
              )}
              {hcSkippedTrims.length > 0 && (
                <p className="text-[11px] text-amber-500" data-testid="kit-hc-trim-warning">
                  {hcSkippedTrims.map((t) => TRIM_LABELS[t]).join(", ")} isn't a KDP hardcover size — those trims ship paperback-only.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Paper</Label>
              <select value={paper} onChange={(e) => setPaper(e.target.value)} data-testid="kit-paper-select"
                className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm">
                <option value="cream">Cream (novels)</option>
                <option value="white">White</option>
                {binding !== "hardcover" && <option value="groundwood">Groundwood (paperback only)</option>}
              </select>
              {binding === "both" && paper === "groundwood" && (
                <p className="text-[11px] text-amber-500" data-testid="kit-groundwood-warning">
                  Groundwood is paperback-only — the hardback will use paperback covers only. Pick White or Cream for a hardback.
                </p>
              )}
            </div>
          </div>

          {hasActs && (
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label className="font-normal">Act divider pages</Label>
                <p className="text-[11px] text-muted-foreground">A full page for each act (e.g. "Act I") before its chapters, in the paperback and eBook.</p>
              </div>
              <Switch checked={includeActs} onCheckedChange={setIncludeActs} data-testid="kit-include-acts" />
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Back-cover blurb <span className="text-muted-foreground font-normal">(printed on the wrap cover)</span></Label>
            <textarea value={blurb} onChange={(e) => setBlurb(e.target.value)} rows={5} data-testid="kit-blurb"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Leave blank to use your listing blurb" />
            <p className="text-[11px] text-muted-foreground">Saved with the book, so the wrap cover dialog uses the same blurb.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Your legal name <span className="text-muted-foreground font-normal">(for the certificate)</span></Label>
            <Input value={legalName} onChange={(e) => setLegalName(e.target.value)}
              placeholder="Falls back to your pen name if left blank" data-testid="kit-legal-name" />
          </div>

          <p className="text-[11px] text-muted-foreground">
            {hasMeta
              ? "Building runs in the background — long books can take a few minutes. Keep this window open."
              : "No listing metadata yet — we'll generate it as part of the kit (3 credits). Building runs in the background and can take a few minutes for long books."}
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={build} disabled={busy} data-testid="kit-build" className="gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {busy ? "Building your kit…" : "Build & download kit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
